import {
  useRef,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  FC,
  useMemo,
  CSSProperties,
  memo,
  useId,
} from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import "./tooltip.scss";

interface TooltipProps {
  delay?: number;
  direction?: "right" | "top" | "left" | "center";
  content?: string;
  children: ReactNode;
  maxWidth?: number;
  className?: string;
  wrapperClass?: string;
  zIndex?: number;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "summary",
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(", ");

const addDescribedBy = (currentValue: string | null, tooltipId: string) => {
  const ids = new Set((currentValue ?? "").split(/\s+/).filter(Boolean));
  ids.add(tooltipId);
  return Array.from(ids).join(" ");
};

const removeDescribedBy = (currentValue: string | null, tooltipId: string) => {
  return (currentValue ?? "")
    .split(/\s+/)
    .filter((id) => id && id !== tooltipId)
    .join(" ");
};

const CustomTooltipComponent: FC<TooltipProps> = (props: TooltipProps) => {
  const [active, setActive] = useState<boolean>(false);
  const [keyboardTarget, setKeyboardTarget] = useState<HTMLElement | null>(null);
  const [isWrapperFocusable, setIsWrapperFocusable] = useState<boolean>(false);
  const [leftPosition, setLeftPosition] = useState<number>();
  const [topPosition, setTopPosition] = useState<number>();
  const tooltipContainer = useRef<HTMLSpanElement>(null);
  const tooltipWrapper = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();

  useLayoutEffect(() => {
    if (!active || !tooltipContainer.current || !tooltipWrapper.current) {
      return;
    }
    const rectangle = tooltipWrapper.current.getBoundingClientRect();
    const tooltipHeight = tooltipContainer.current?.offsetHeight || 0;
    const tooltipWidth = tooltipContainer.current?.offsetWidth || 0;

    switch (props.direction) {
      case "right":
        setLeftPosition(rectangle.left + rectangle.width + 12);
        setTopPosition(rectangle.top + rectangle.height / 2 - tooltipHeight / 2);
        break;
      case "top":
        setLeftPosition(rectangle.left + rectangle.width / 2 - tooltipWidth / 2 - 3);
        setTopPosition(rectangle.top - tooltipHeight - 12);
        break;
      case "left":
        setLeftPosition(rectangle.left - tooltipWidth - 12);
        setTopPosition(rectangle.top + rectangle.height / 2 - tooltipHeight / 2);
        break;
      case "center":
        setLeftPosition(rectangle.left + rectangle.width / 2 - tooltipWidth / 2 - 3);
        setTopPosition(rectangle.top + rectangle.height);
        break;
      default:
        setLeftPosition(rectangle.left + rectangle.width / 2 - tooltipWidth / 2 - 6);
        setTopPosition(rectangle.top + rectangle.height);
    }
  }, [active, props.content, props.direction, props.maxWidth]);

  useLayoutEffect(() => {
    if (!props.content || !tooltipWrapper.current) {
      setKeyboardTarget(null);
      setIsWrapperFocusable(false);
      return;
    }

    const wrapper = tooltipWrapper.current;
    const focusableDescendant = wrapper.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    const focusableAncestor =
      wrapper.parentElement?.closest<HTMLElement>(FOCUSABLE_SELECTOR) ?? null;
    const nextKeyboardTarget = focusableDescendant ?? focusableAncestor ?? wrapper;

    setKeyboardTarget(nextKeyboardTarget);
    setIsWrapperFocusable(nextKeyboardTarget === wrapper);
  }, [props.children, props.content]);

  const showTip = useCallback(() => {
    if (!props.content) {
      return;
    }
    setActive(true);
  }, [props.content]);

  const hideTip = useCallback(() => {
    setActive(false);
  }, []);

  const hideOnEscape = useCallback(
    (key: string) => {
      if (key === "Escape") {
        hideTip();
      }
    },
    [hideTip],
  );

  useEffect(() => {
    const wrapper = tooltipWrapper.current;
    if (!props.content || !keyboardTarget || !wrapper) {
      return;
    }

    if (keyboardTarget === wrapper || wrapper.contains(keyboardTarget)) {
      return;
    }

    const handleFocus = () => {
      showTip();
    };
    const handleBlur = () => {
      hideTip();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      hideOnEscape(event.key);
    };

    keyboardTarget.addEventListener("focus", handleFocus);
    keyboardTarget.addEventListener("blur", handleBlur);
    keyboardTarget.addEventListener("keydown", handleKeyDown);

    return () => {
      keyboardTarget.removeEventListener("focus", handleFocus);
      keyboardTarget.removeEventListener("blur", handleBlur);
      keyboardTarget.removeEventListener("keydown", handleKeyDown);
    };
  }, [hideOnEscape, hideTip, keyboardTarget, props.content, showTip]);

  useEffect(() => {
    if (!props.content || !keyboardTarget) {
      return;
    }

    if (!active) {
      const describedByValue = removeDescribedBy(
        keyboardTarget.getAttribute("aria-describedby"),
        tooltipId,
      );

      if (describedByValue) {
        keyboardTarget.setAttribute("aria-describedby", describedByValue);
      } else {
        keyboardTarget.removeAttribute("aria-describedby");
      }

      return;
    }

    keyboardTarget.setAttribute(
      "aria-describedby",
      addDescribedBy(keyboardTarget.getAttribute("aria-describedby"), tooltipId),
    );

    return () => {
      const describedByValue = removeDescribedBy(
        keyboardTarget.getAttribute("aria-describedby"),
        tooltipId,
      );

      if (describedByValue) {
        keyboardTarget.setAttribute("aria-describedby", describedByValue);
      } else {
        keyboardTarget.removeAttribute("aria-describedby");
      }
    };
  }, [active, keyboardTarget, props.content, tooltipId]);

  const tooltipStyle = useMemo(() => {
    return {
      left: leftPosition,
      top: topPosition,
      position: "fixed",
      maxWidth: props.maxWidth,
      zIndex: props.zIndex,
    } as CSSProperties;
  }, [leftPosition, props.maxWidth, topPosition, props.zIndex]);

  return (
    <span
      className={classNames("tooltip-wrapper", props.wrapperClass)}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      onFocus={showTip}
      onBlur={hideTip}
      onKeyDown={(event) => {
        hideOnEscape(event.key);
      }}
      role={props.content && isWrapperFocusable ? "button" : undefined}
      tabIndex={props.content && isWrapperFocusable ? 0 : undefined}
      ref={tooltipWrapper}
    >
      {props.children}
      {active &&
        ReactDOM.createPortal(
          <span
            className={classNames(
              "tooltip-tip",
              props.direction || "bottom",
              { show: active },
              props.className,
            )}
            style={tooltipStyle}
            ref={tooltipContainer}
            role="tooltip"
            id={tooltipId}
          >
            {props.content}
          </span>,
          document.body,
        )}
    </span>
  );
};

export const CustomTooltip = memo(CustomTooltipComponent);
